// src/services/DashboardService.js
import axios from "axios";

// Konfigurasi Base URL API Dashboard (sesuaikan env variabel dengan framework Anda)
const DASHBOARD_API_URL =
  // process.env.NEXT_PUBLIC_DASHBOARD_API_URL ||
  process.env.REACT_APP_API_URL_DASHBOARD_UP;

// ----- Core Fetch Functions -----
async function baseFetch(baseUrl, endpoint, options = {}) {
  const res = await fetch(`${baseUrl}${endpoint}`, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...(options.headers || {}), // jika ada tambahan header custom
    },
    credentials: "include", // penting: kirim cookie cross-domain
  });

  if (!res.ok) {
    console.error(`❌ Fetch error: ${res.status} ${res.statusText}`);
    throw new Error(`API error: ${res.status} ${res.statusText}`);
  }

  return res.json();
}

/**
 * Wrapper untuk request ke backend Dashboard.
 */
function dashboardFetch(endpoint, options = {}) {
  return baseFetch(DASHBOARD_API_URL, endpoint, options);
}

// ----- Service Class -----
export default class DashboardService {
  static async getSummaryEkspor(
    kdUpt,
    tglAwal,
    tglAkhir,
    negara = "",
    upt = "",
    komoditas = ""
  ) {
    const params = new URLSearchParams();
    if (negara) params.set("negara", negara);
    if (upt) params.set("upt", upt);
    if (komoditas) params.set("komoditas", komoditas);

    const query = params.toString() ? `?${params.toString()}` : "";
    return dashboardFetch(
      `/ekspor/summary/${kdUpt}/${tglAwal}/${tglAkhir}${query}`
    );
  }

  static async getEksporHarian(kdUpt, tglAwal, tglAkhir) {
    return dashboardFetch(
      `/ekspor/harian/${kdUpt}/${tglAwal}/${tglAkhir}`
    );
  }

  static async getEksporBulanan(kdUpt, tglAwal, tglAkhir) {
    return dashboardFetch(
      `/ekspor/bulanan/${kdUpt}/${tglAwal}/${tglAkhir}`
    );
  }

  static async getRekapIzinPrimer(startDate, endDate, limit) {
    const params = new URLSearchParams();
    if (startDate) params.append("startDate", startDate);
    if (endDate) params.append("endDate", endDate);
    if (limit !== undefined) params.append("limit", limit.toString());

    return dashboardFetch(
      `/primer/rekap-izin-primer?${params.toString()}`
    );
  }

  static async getPropinsiPerIzin(startDate, endDate, kdIzin, limit) {
    const params = new URLSearchParams();
    params.append("startDate", startDate);
    params.append("endDate", endDate);
    params.append("kdIzin", kdIzin);
    if (limit) params.append("limit", limit.toString());

    return dashboardFetch(
      `/primer/propinsi-per-izin?${params.toString()}`
    );
  }

  // ----- Tambahan Method untuk SKP -----
  // 🟢 1️⃣ Resume SKP
  static async getResumeSKP(startDate, endDate) {
    const params = new URLSearchParams();
    if (startDate) params.append("startDate", startDate);
    if (endDate) params.append("endDate", endDate);

    return dashboardFetch(`/skp/resume-skp?${params.toString()}`);
  }

  // 🟢 2️⃣ Tren Bulanan
  static async getTrenBulananSKP(startDate, endDate) {
    const params = new URLSearchParams();
    if (startDate) params.append("startDate", startDate);
    if (endDate) params.append("endDate", endDate);

    return dashboardFetch(`/skp/tren-bulanan?${params.toString()}`);
  }

  // 🟢 3️⃣ Komposisi Peringkat
  static async getKomposisiPeringkatSKP(startDate, endDate) {
    const params = new URLSearchParams();
    if (startDate) params.append("startDate", startDate);
    if (endDate) params.append("endDate", endDate);

    return dashboardFetch(`/skp/komposisi-peringkat?${params.toString()}`);
  }

  // 🟢 4️⃣ Top 10 UPI
  static async getTopUPISKP(startDate, endDate) {
    const params = new URLSearchParams();
    if (startDate) params.append("startDate", startDate);
    if (endDate) params.append("endDate", endDate);

    return dashboardFetch(`/skp/top-upi?${params.toString()}`);
  }

  // 🟢 5️⃣ Top 10 Provinsi
  static async getTopProvinsiSKP(startDate, endDate) {
    const params = new URLSearchParams();
    if (startDate) params.append("startDate", startDate);
    if (endDate) params.append("endDate", endDate);

    return dashboardFetch(`/skp/top-provinsi?${params.toString()}`);
  }

  // 🟢 6️⃣ Komposisi Jenis Permohonan
  static async getKomposisiPermohonanSKP(startDate, endDate) {
    const params = new URLSearchParams();
    if (startDate) params.append("startDate", startDate);
    if (endDate) params.append("endDate", endDate);

    return dashboardFetch(`/skp/komposisi-permohonan?${params.toString()}`);
  }

  // 🟢 7️⃣ Distribusi Skala Usaha
  static async getDistribusiSkalaUsahaSKP(startDate, endDate) {
    const params = new URLSearchParams();
    if (startDate) params.append("startDate", startDate);
    if (endDate) params.append("endDate", endDate);

    return dashboardFetch(`/skp/distribusi-skala?${params.toString()}`);
  }

  // 🟢 8️⃣ Komposisi Olahan
  static async getKomposisiOlahanSKP(startDate, endDate) {
    const params = new URLSearchParams();
    if (startDate) params.append("startDate", startDate);
    if (endDate) params.append("endDate", endDate);

    return dashboardFetch(`/skp/komposisi-olahan?${params.toString()}`);
  }

  // 🟢 9️⃣ Top 10 Kabupaten/Kota
  static async getTopKabupatenSKP(startDate, endDate) {
    const params = new URLSearchParams();
    if (startDate) params.append("startDate", startDate);
    if (endDate) params.append("endDate", endDate);

    return dashboardFetch(`/skp/top-kabupaten?${params.toString()}`);
  }

  // 🟢 9️⃣ GeoSpasial SKP
  static async getGeoSpasialSKP(startDate, endDate) {
    const params = new URLSearchParams();
    if (startDate) params.append("startDate", startDate);
    if (endDate) params.append("endDate", endDate);

    return dashboardFetch(`/skp/geo-skp?${params.toString()}`);
  }

  // 🟢 9️⃣ GeoSpasial HACCP
  static async getGeoSpasialHACCP(startDate, endDate) {
    const params = new URLSearchParams();
    if (startDate) params.append("startDate", startDate);
    if (endDate) params.append("endDate", endDate);

    return dashboardFetch(`/haccp/geo-haccp?${params.toString()}`);
  }

  // 🟢 9️⃣ GeoSpasial UPI
  static async getGeoSpasialUPI(startDate, endDate) {
    const params = new URLSearchParams();
    if (startDate) params.append("startDate", startDate);
    if (endDate) params.append("endDate", endDate);

    return dashboardFetch(`/haccp/geo-upi?${params.toString()}`);
  }

  // 🟢 🔟 UPT BPPMHKP Locations
  static async getUPTBPPMHKP() {
    return dashboardFetch(`/common/upt-bppmhkp`);
  }
}